import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Unit, UnitType } from '../types/user';
import apiService from '../services/api';

// Async thunks
export const fetchUnits = createAsyncThunk(
  'units/fetchUnits',
  async () => {
    const response = await apiService.getUnits();
    return response;
  }
);

export const fetchUnit = createAsyncThunk(
  'units/fetchUnit',
  async (unitId: number) => {
    const response = await apiService.getUnit(unitId);
    return response;
  }
);

export const createUnit = createAsyncThunk(
  'units/createUnit',
  async (unitData: Partial<Unit>) => {
    const response = await apiService.createUnit(unitData);
    return response;
  }
);

export const updateUnit = createAsyncThunk(
  'units/updateUnit',
  async ({ unitId, data }: { unitId: number; data: Partial<Unit> }) => {
    const response = await apiService.updateUnit(unitId, data);
    return response;
  }
);

export const deleteUnit = createAsyncThunk(
  'units/deleteUnit',
  async (unitId: number) => {
    await apiService.deleteUnit(unitId);
    return unitId;
  }
);

export const fetchUnitStaff = createAsyncThunk(
  'units/fetchUnitStaff',
  async (unitId: number) => {
    const response = await apiService.getUnitStaff(unitId);
    return { unitId, staff: response };
  }
);

export const fetchUnitCurrentShifts = createAsyncThunk(
  'units/fetchUnitCurrentShifts',
  async (unitId: number) => {
    const response = await apiService.getUnitCurrentShifts(unitId);
    return { unitId, shifts: response };
  }
);

// Unit Type async thunks (CSV configurable)
export const fetchUnitTypes = createAsyncThunk(
  'units/fetchUnitTypes',
  async () => {
    const response = await apiService.getUnitTypes();
    return response;
  }
);

export const fetchUnitType = createAsyncThunk(
  'units/fetchUnitType',
  async (unitTypeId: number) => {
    const response = await apiService.getUnitType(unitTypeId);
    return response;
  }
);

export const fetchUnitTypeUnits = createAsyncThunk(
  'units/fetchUnitTypeUnits',
  async (unitTypeId: number) => {
    const response = await apiService.getUnitTypeUnits(unitTypeId);
    return { unitTypeId, units: response };
  }
);

// Unit state interface
interface UnitsState {
  units: Unit[];
  selectedUnit: Unit | null;
  unitStaff: { [unitId: number]: any };
  unitCurrentShifts: { [unitId: number]: any[] };
  unitTypes: UnitType[];
  selectedUnitType: UnitType | null;
  unitTypeUnits: { [unitTypeId: number]: Unit[] };
  loading: boolean;
  unitTypesLoading: boolean;
  error: string | null;
}

const initialState: UnitsState = {
  units: [],
  selectedUnit: null,
  unitStaff: {},
  unitCurrentShifts: {},
  unitTypes: [],
  selectedUnitType: null,
  unitTypeUnits: {},
  loading: false,
  unitTypesLoading: false,
  error: null,
};

const unitsSlice = createSlice({
  name: 'units',
  initialState,
  reducers: {
    setSelectedUnit: (state, action: PayloadAction<Unit | null>) => {
      state.selectedUnit = action.payload;
    },
    setSelectedUnitType: (state, action: PayloadAction<UnitType | null>) => {
      state.selectedUnitType = action.payload;
    },
    clearUnitsError: (state) => {
      state.error = null;
    },
    clearUnitsState: (state) => {
      state.units = [];
      state.selectedUnit = null;
      state.unitStaff = {};
      state.unitCurrentShifts = {};
      state.unitTypes = [];
      state.selectedUnitType = null;
      state.unitTypeUnits = {};
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch units
    builder
      .addCase(fetchUnits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnits.fulfilled, (state, action) => {
        state.loading = false;
        state.units = action.payload;
      })
      .addCase(fetchUnits.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch units';
      });

    // Fetch single unit
    builder
      .addCase(fetchUnit.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnit.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedUnit = action.payload;
        // Update units list if unit exists
        const index = state.units.findIndex(unit => unit.id === action.payload.id);
        if (index !== -1) {
          state.units[index] = action.payload;
        }
      })
      .addCase(fetchUnit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch unit';
      });

    // Create unit
    builder
      .addCase(createUnit.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createUnit.fulfilled, (state, action) => {
        state.loading = false;
        state.units.push(action.payload);
      })
      .addCase(createUnit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create unit';
      });

    // Update unit
    builder
      .addCase(updateUnit.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUnit.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.units.findIndex(unit => unit.id === action.payload.id);
        if (index !== -1) {
          state.units[index] = action.payload;
        }
        if (state.selectedUnit?.id === action.payload.id) {
          state.selectedUnit = action.payload;
        }
      })
      .addCase(updateUnit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update unit';
      });

    // Delete unit
    builder
      .addCase(deleteUnit.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUnit.fulfilled, (state, action) => {
        state.loading = false;
        state.units = state.units.filter(unit => unit.id !== action.payload);
        if (state.selectedUnit?.id === action.payload) {
          state.selectedUnit = null;
        }
        // Clean up related data
        delete state.unitStaff[action.payload];
        delete state.unitCurrentShifts[action.payload];
      })
      .addCase(deleteUnit.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete unit';
      });

    // Fetch unit staff
    builder
      .addCase(fetchUnitStaff.fulfilled, (state, action) => {
        state.unitStaff[action.payload.unitId] = action.payload.staff;
      })
      .addCase(fetchUnitStaff.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch unit staff';
      });

    // Fetch unit current shifts
    builder
      .addCase(fetchUnitCurrentShifts.fulfilled, (state, action) => {
        state.unitCurrentShifts[action.payload.unitId] = action.payload.shifts;
      })
      .addCase(fetchUnitCurrentShifts.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch unit current shifts';
      });

    // Fetch unit types (CSV configurable)
    builder
      .addCase(fetchUnitTypes.pending, (state) => {
        state.unitTypesLoading = true;
        state.error = null;
      })
      .addCase(fetchUnitTypes.fulfilled, (state, action) => {
        state.unitTypesLoading = false;
        state.unitTypes = action.payload;
      })
      .addCase(fetchUnitTypes.rejected, (state, action) => {
        state.unitTypesLoading = false;
        state.error = action.error.message || 'Failed to fetch unit types';
      });

    // Fetch single unit type
    builder
      .addCase(fetchUnitType.pending, (state) => {
        state.unitTypesLoading = true;
        state.error = null;
      })
      .addCase(fetchUnitType.fulfilled, (state, action) => {
        state.unitTypesLoading = false;
        state.selectedUnitType = action.payload;
        // Update unit types list if unit type exists
        const index = state.unitTypes.findIndex(type => type.id === action.payload.id);
        if (index !== -1) {
          state.unitTypes[index] = action.payload;
        }
      })
      .addCase(fetchUnitType.rejected, (state, action) => {
        state.unitTypesLoading = false;
        state.error = action.error.message || 'Failed to fetch unit type';
      });

    // Fetch unit type units
    builder
      .addCase(fetchUnitTypeUnits.fulfilled, (state, action) => {
        state.unitTypeUnits[action.payload.unitTypeId] = action.payload.units;
      })
      .addCase(fetchUnitTypeUnits.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch unit type units';
      });
  },
});

// Selectors
export const selectUnits = (state: any) => state.units.units;
export const selectSelectedUnit = (state: any) => state.units.selectedUnit;
export const selectUnitsLoading = (state: any) => state.units.loading;
export const selectUnitsError = (state: any) => state.units.error;
export const selectUnitStaff = (state: any, unitId: number) => state.units.unitStaff[unitId];
export const selectUnitCurrentShifts = (state: any, unitId: number) => state.units.unitCurrentShifts[unitId];

// Unit Type selectors (CSV configurable)
export const selectUnitTypes = (state: any) => state.units.unitTypes;
export const selectSelectedUnitType = (state: any) => state.units.selectedUnitType;
export const selectUnitTypesLoading = (state: any) => state.units.unitTypesLoading;
export const selectUnitTypeUnits = (state: any, unitTypeId: number) => state.units.unitTypeUnits[unitTypeId];

// Filter units by type (using dynamic unit type)
export const selectUnitsByType = (state: any, unitTypeId: number) => 
  state.units.units.filter((unit: Unit) => unit.unit_type === unitTypeId);

// Filter units by unit type code (backward compatibility)
export const selectUnitsByTypeCode = (state: any, unitTypeCode: string) => 
  state.units.units.filter((unit: Unit) => unit.unit_type_code === unitTypeCode);

// Filter unit types by category
export const selectUnitTypesByCategory = (state: any, category: string) =>
  state.units.unitTypes.filter((type: UnitType) => type.category === category);

// Get units by department
export const selectUnitsByDepartment = (state: any, departmentId: number) =>
  state.units.units.filter((unit: Unit) => unit.managing_department === departmentId);

// Get available unit type categories
export const selectUnitTypeCategories = (state: any) => {
  const categories = new Set(state.units.unitTypes.map((type: UnitType) => type.category));
  return Array.from(categories);
};

export const { setSelectedUnit, setSelectedUnitType, clearUnitsError, clearUnitsState } = unitsSlice.actions;
export default unitsSlice.reducer;